import React, { useContext, useEffect, useRef, useState } from "react";
import {
  SdfFocusPane, SdfTooltip, SdfCloseButton, SdfIcon, SdfButton,
  SdfActionMenu, SdfMenu, SdfMenuItem
} from "@waypoint/react-components";
import moment from "moment-timezone";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkEmoji from "remark-emoji";
import supersub from "remark-supersub";
import { remarkMark as remarkHighlight } from "remark-mark-highlight";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import RemoveMarkdown from "remove-markdown";

import { AssistContext } from "./AssistProvider";
import AssistIcon from "./AssistIcon";
import AssistTextArea from "./AssistTextArea";
import AssistAdminWelcome from "./AssistAdminWelcome.png";
import AssistUserWelcome from "./AssistUserWelcome.png";
import "./Assist.scss";

/**
 * @param {{ children: React.ReactNode, href: string }} props
 */
const LinkRenderer = ({ children, href }) => (
  <a href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

const CodeRenderer = ({ children, className, node, ...rest }) => {
  const match = /language-(\w+)/.exec(className || '');
  const childText = String(children);

  if (match || childText.includes("\n")) {
    const onClick = () => {
      navigator.clipboard.writeText(childText);
    };

    return (
      <div className="AssistCodeblock relative px-2 pt-8 pb-2 bg-alert-neutral-100 rounded-lg text-default">
        <SdfTooltip
          positionContext="ELEMENT" trigger="click" state="neutral" attachmentPoint="bottom"
          className="absolute m-2 top-0 right-0"
          {...{ onClick }}
        >
          <SdfIcon slot="tooltip-target" icon="action-copy" />
          <span className="">Copied</span>
        </SdfTooltip>
        <SyntaxHighlighter
          {...rest}
          PreTag={({ children }) => children}
          children={childText.replace(/\n$/, '')}
          language={match ? match[1] : undefined}
        />
      </div>
    );
  }



  return (
    <span
      {...rest}
      className={
        `bg-alert-neutral-100 ${childText.includes(" ") ? "break-words" : "break-all"}${className ? ` ${className}` : ""}`
      }
    >
      {children}
    </span>
  );
};

/**
 * @param {{ className?: string }} props
 */
const AgentAvatar = ({ className, ...rest }) => (
  <div
    className={`AssistAvatar relative bg-alert-info-800 rounded-full${className ? ` ${className}` : ""}`}
    {...rest}
  >
    <div className="AssistAvatarAgent absolute inset-0 flex items-center justify-center text-heading-04">
      <SdfIcon icon="brand-adp-assist" />
    </div>
  </div>
);

/**
 * @param {{ text: string }} props
 */
const AssistMarkdown = ({ text }) => (
  <div className="AssistMarkdown text-body-02">
    <ReactMarkdown
      remarkPlugins={[
        [remarkGfm, { singleTilde: false }],
        supersub, remarkEmoji, remarkHighlight
      ]}
      components={{ a: LinkRenderer, code: CodeRenderer }}
    >
      {text}
    </ReactMarkdown>
  </div>
);

/**
 * @param {{ message: { text: string }, isAdmin: boolean }} props
 */
const AssistantWelcome = ({ message, isAdmin, ...rest }) => (
  <div className="AssistWelcome w-1/2 p-4 border-solid border-2 rounded-t-lg" {...rest}>
    {isAdmin ?
      <img src={AssistAdminWelcome} alt="Welcome" className="max-w-full" /> :
      <img src={AssistUserWelcome} alt="Welcome" className="max-w-full" />
    }
    <h1 className="mt-2 mb-0 text-heading-04">Welcome!</h1>
    <AssistMarkdown text={message.text} />
  </div>
);

/**
 * @param {{
 *  icon: string,
 *  reaction?: "up" | "down",
 *  updateFeedback: (id: string, reaction: "up" | "down") => Promise<void>
 * }} props
 */
const FeedbackButton = ({ icon, id, reaction, updateFeedback }) => {
  const isLike = icon === "feedback-like";
  const isClicked = reaction === (isLike ? "up" : "down");
  const buttonTitle = isClicked ? "Remove Feedback" : isLike ? "Like" : "Dislike";

  const onClick = () => {
    if (isClicked) {
      updateFeedback(id, null);
    } else if (isLike) {
      updateFeedback(id, "up");
    } else {
      updateFeedback(id, "down");
    }
  };

  if (!!reaction && !isClicked) {
    return (
    <SdfButton
      disabled
      iconOnly size="sm"
      emphasis={isClicked ? "secondary" : "tertiary"}
      {...{ icon, buttonTitle, onClick }}
    />
  );
  }

  return (
    <SdfButton
      iconOnly size="sm"
      emphasis={isClicked ? "secondary" : "tertiary"}
      {...{ icon, buttonTitle, onClick }}
    />
  );
};

/**
 * 
 * @param {{
 *  message: { text: string, timestamp: Date },
 *  initials: string,
 *  scrollToMessage: (position: number) => void
 * }} props
 */
const UserMessage = ({ message, initials, scrollToMessage }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (scrollToMessage && ref.current) {
      scrollToMessage(ref.current.offsetTop);
    }
  }, [scrollToMessage]);

  return (
    <div ref={ref} className="w-1/2 ml-auto pt-4 flex justify-end">
      <div aria-label="You:" className="flex gap-2 justify-end">
        <div className="p-2 flex flex-col bg-alert-neutral-100 rounded-lg rounded-tr-none text-body-01">
          <div>{message.text}</div>
          <div className="ml-auto text-tertiary italic">
            {moment(message.timestamp).tz("America/New_York").format("YYYY-MM-DD h:mm A")}
          </div>
        </div>
        <div aria-hidden="true">
          <div className="AssistAvatar relative bg-alert-neutral-100 rounded-full">
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-heading-04">{initials}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * @param {{
 *  message: {
 *    id: string,
 *    text: string,
 *    timestamp: Date,
 *    status: "pending" | "partial" | "complete" | "error",
 *    reaction?: "up" | "down"
 *    isGreeting?: boolean
 *  },
 *  updateFeedback: (id: string, reaction: "up" | "down") => Promise<void>,
 *  isAdmin: boolean
 * }} props
 */
const AssistantMessage = ({ message, updateFeedback, isAdmin }) => {
  // TODO display text slowly to mimic typing?

  // TODO - copy whole message button
  // TODO - use remove-markdown or a similar library to strip markdown for copy whole message option


  const copyMessageMarkdown = () => {
    navigator.clipboard.writeText(message.text);
  }

  const copyMessagePlain = () => {
    navigator.clipboard.writeText(RemoveMarkdown(message.text, { stripListLeaders: false }));
  }


  return (
    <div className="pt-4 flex justify-start gap-4">
      <AgentAvatar aria-hidden="true" className="flex-shrink-0" />
      <div aria-label="ADP Assist:" className="flex-grow flex flex-col min-w-0">
        <div className="mb-2 flex gap-2">
          <div className="text-heading-03">ADP Assist</div>
          <SdfActionMenu
            label="Options" size="sm" variant="text" emphasis="tertiary"
            icon="action-more-horiz" portalEnabled={true}
          >
            <SdfMenu>
              <SdfMenuItem onClick={copyMessagePlain}>Copy as text</SdfMenuItem>
              <SdfMenuItem onClick={copyMessageMarkdown}>Copy as markdown</SdfMenuItem>
            </SdfMenu>
          </SdfActionMenu>
        </div>
        {/* Add title and copy button here */}
        <div className="flex flex-col">
          {message.isGreeting ? 
            <AssistantWelcome message={message} {...{ isAdmin }} /> :
            <AssistMarkdown text={message.text} />
          }
          <div className="text-tertiary italic text-body-01">
            {moment(message.timestamp).tz("America/New_York").format("YYYY-MM-DD h:mm A")}
          </div>
        </div>
        {message.status === "complete" && (
          <div className="mt-auto flex gap-2">
            <FeedbackButton
              icon="feedback-like"
              id={message.id}
              reaction={message.reaction}
              updateFeedback={updateFeedback}
            />
            <FeedbackButton
              icon="feedback-dislike"
              id={message.id}
              reaction={message.reaction}
              updateFeedback={updateFeedback}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const PendingMessage = () => (
  <div className="pt-4 flex justify-start gap-4 text-body-02">
    <AgentAvatar aria-hidden="true" />
    <div aria-label="ADP Assist message pending" className="AssistPending" />
  </div>
);

const AssistModal = () => {
  const { messages, updateFeedback, modalOpen, setModalOpen, isAdmin, initials } = useContext(AssistContext);
  const [scrollFinished, setScrollFinished] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const scrollRef = useRef(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const pending = !!messages.find((message) => message.status === "pending");
  const displayMessages = [...messages]
    .filter((message) => message.status != "pending")
    .sort((a, b) => a.timestamp - b.timestamp);

  const lastUserMessage = displayMessages.toReversed().find((message) => message.type === "user");

  if (lastUserMessage) {
    lastUserMessage.isLast = true;
  }

  useEffect(() => {
    if (displayMessages.length !== messageCount) {
      setMessageCount(displayMessages.length);
      setScrollFinished(false);
    }
  }, [displayMessages, messageCount]);

  /** @type {(position: number) => void} */
  const scrollToMessage = (position) => {
    if (scrollRef.current && !scrollFinished) {
      scrollRef.current.scrollTop = position;
      setScrollFinished(true);
    }
  };

  return (
    <SdfFocusPane closeable hideHeader hideFooter size="lg" visible={modalOpen}>
      <div className="relative h-3xl flex flex-col gap-1 overflow-hidden">
        <SdfCloseButton size="sm" className="absolute top-0 right-0 mr-4 z-10" onSdfClose={() => setModalOpen(false)} />
        <div className="ml-4 mr-12 flex gap-4">
          <div className="flex gap-4 items-center">
            <AssistIcon className="text-heading-06" />
            <span className="text-heading-05">ADP Assist</span>
          </div>
          <div className="flex gap-1 ml-auto mt-auto">
            <span className="mt-auto text-body-01 text-secondary italic">Content is AI-generated. Always verify before relying on this information.</span>
            <SdfTooltip portalEnabled tooltipId="tooltip-content">
              <SdfIcon slot="tooltip-target" icon="action-help" className="mt-auto text-body-02 text-action-primary" />
              <span id="tooltip-content">ADP Assist is created by artificial intelligence (AI). The responses are based on information provided by ADP within developer documentation. ADP does not make any representations or warranties regarding and is not responsible for the accuracy or completeness of the information provided or the responses based thereon.</span>
            </SdfTooltip>
          </div>
        </div>
        <div className="AssistModalContent mx-8 mb-8 flex-grow flex flex-col overflow-hidden border-solid border-1 rounded-lg">
          <div ref={scrollRef} className="relative flex-grow overflow-x-hidden overflow-y-scroll">
            <div className="px-4 pb-4 flex flex-col">
              {displayMessages.map((message) => {
                if (message.type === "assistant") {
                  return <AssistantMessage key={message.id} {...{ message, updateFeedback, isAdmin }} />;
                } else {
                  if (message.isLast) {
                    return <UserMessage key={message.id} {...{ message, initials, scrollToMessage }} />;
                  } else {
                    return <UserMessage key={message.id} {...{ message, initials }} />;
                  }
                }
              })}
              {pending && <PendingMessage />}
            </div>
          </div>
          <div className="px-8 pt-8 bg-alert-info-50">
            <AssistTextArea isModalText />
          </div>
        </div>
      </div>
    </SdfFocusPane>
  )
};

export default AssistModal;